import { useState, useEffect } from "react";
import {
  X,
  Send,
  Loader2,
  MessageSquare,
  CheckCircle,
  AlertCircle,
  User,
  ChevronDown,
} from "lucide-react";
import { showToast } from "@/components/ui/Toast";
import {
  NotificationContact,
  sendDeliverableNotification,
  Deliverable,
  DeliveryhubTemplate,
  getDeliveryhubTemplates,
} from "@/actions/delivery-hub";
import { getSchoolNotificationContacts } from "@/actions/delivery-hub";

interface NotifyClientsSliderProps {
  isOpen: boolean;
  onClose: () => void;
  deliverable: Deliverable | null;
  schoolId: number;
  onSuccess?: () => void;
}

// Map deliverable type to template deliverable_type
const getTemplateType = (deliverable: Deliverable | null): string => {
  if (!deliverable) return "blog";

  const type = deliverable.type;
  if (type === "blog") return "blog";

  if (type === "video") {
    const title = (deliverable.title || "").toLowerCase();
    const description = (deliverable.description || "").toLowerCase();
    if (title.includes("educator") || description.includes("educator")) {
      return "educator_video";
    }
    return "parent_video";
  }

  return "blog";
};

const NotifyClientsSlider = ({
  isOpen,
  onClose,
  deliverable,
  schoolId,
  onSuccess,
}: NotifyClientsSliderProps) => {
  const [contacts, setContacts] = useState<NotificationContact[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [results, setResults] = useState<{ success: number; failed: number } | null>(null);

  // Template states
  const [templates, setTemplates] = useState<DeliveryhubTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<DeliveryhubTemplate | null>(null);
  const [templateDropdownOpen, setTemplateDropdownOpen] = useState(false);
  const [customLink, setCustomLink] = useState("");

  // Animation states
  const [shouldRender, setShouldRender] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  // Handle animation
  useEffect(() => {
    if (isOpen && !shouldRender) {
      setShouldRender(true);
      setSent(false);
      setResults(null);
      setTimeout(() => {
        setIsAnimating(true);
      }, 50);
    } else if (!isOpen && shouldRender) {
      setIsAnimating(false);
      const timer = setTimeout(() => {
        setShouldRender(false);
        setSelectedTemplate(null);
        setTemplateDropdownOpen(false);
        setCustomLink("");
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen, shouldRender]);

  // Fetch contacts and templates when opened
  useEffect(() => {
    if (isOpen && schoolId) {
      fetchData();
    }
  }, [isOpen, schoolId, deliverable]);

  // Prevent body scroll
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const fetchData = async () => {
    setLoading(true);

    // Fetch contacts
    const contactsData = await getSchoolNotificationContacts(schoolId);
    const whatsappContacts = (contactsData.contacts as NotificationContact[]).filter(
      (c) => c.phone && c.is_active
    );
    setContacts(whatsappContacts);
    setSelectedIds(new Set(whatsappContacts.map((c) => c.id)));

    // Fetch all templates
    const templatesData = await getDeliveryhubTemplates();
    setTemplates(templatesData);

    // Auto-select matching initial template based on deliverable type
    const templateType = getTemplateType(deliverable);
    const initialTemplate = templatesData.find(
      (t) => t.deliverable_type === templateType && t.notification_stage === "initial"
    );
    if (initialTemplate) {
      setSelectedTemplate(initialTemplate);
    } else if (templatesData.length > 0) {
      setSelectedTemplate(templatesData[0]);
    }

    // Don't pre-fill link - backend generates unique review links per contact
    setCustomLink("");

    setLoading(false);
  };

  const toggleContact = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedIds.size === contacts.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(contacts.map((c) => c.id)));
    }
  };

  const handleSend = async () => {
    if (!deliverable || selectedIds.size === 0 || !selectedTemplate) return;

    setSending(true);
    const response = await sendDeliverableNotification(
      deliverable.id,
      Array.from(selectedIds),
      selectedTemplate.notification_stage,
      customLink || undefined
    );

    if (response.success && response.results) {
      const successCount = response.results.filter((r) => r.success).length;
      const failedCount = response.results.filter((r) => !r.success).length;
      setResults({ success: successCount, failed: failedCount });
      setSent(true);

      if (successCount > 0 && failedCount === 0) {
        showToast("All notifications sent successfully!", "success");
      } else if (successCount > 0 && failedCount > 0) {
        showToast(`${successCount} sent, ${failedCount} failed`, "warning");
      } else {
        showToast(`Failed to send ${failedCount} notification(s)`, "error");
      }
      onSuccess?.();
    } else {
      showToast(response.message || "Failed to send notifications", "error");
    }
    setSending(false);
  };

  const getStageLabel = (stage: string) => {
    switch (stage) {
      case "initial":
        return "Draft Ready";
      case "reminder":
        return "Reminder";
      case "final":
        return "Published";
      default:
        return stage;
    }
  };

  const getStageColor = (stage: string) => {
    switch (stage) {
      case "initial":
        return "bg-blue-100 text-blue-700";
      case "reminder":
        return "bg-amber-100 text-amber-700";
      case "final":
        return "bg-green-100 text-green-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case "blog":
        return "Blog";
      case "parent_video":
        return "Parent Video";
      case "educator_video":
        return "Educator Video";
      default:
        return type;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case "blog":
        return "bg-purple-100 text-purple-700";
      case "parent_video":
        return "bg-pink-100 text-pink-700";
      case "educator_video":
        return "bg-indigo-100 text-indigo-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  if (!shouldRender) return null;

  return (
    <div className="fixed inset-0 z-[9999]">
      {/* Backdrop */}
      <div
        onClick={onClose}
        className={`fixed inset-0 bg-black/50 transition-opacity duration-300 cursor-pointer ${
          isAnimating ? "opacity-100" : "opacity-0"
        }`}
      />

      {/* Sidebar */}
      <div
        className={`fixed top-0 right-0 h-full w-full max-w-md bg-white shadow-2xl transition-transform duration-300 ease-out ${
          isAnimating ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <MessageSquare size={20} className="text-green-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Notify Clients</h2>
                <p className="text-sm text-gray-500">Send WhatsApp notification</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X size={20} className="text-gray-500" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {sent ? (
              // Result State
              <div className="flex flex-col items-center justify-center h-full text-center">
                <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 ${
                  results && results.success > 0 ? "bg-green-100" : "bg-red-100"
                }`}>
                  {results && results.success > 0 ? (
                    <CheckCircle size={32} className="text-green-600" />
                  ) : (
                    <AlertCircle size={32} className="text-red-600" />
                  )}
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {results && results.success > 0 ? "Notifications Sent!" : "Notifications Failed"}
                </h3>
                {results && (
                  <div className="text-sm mb-6">
                    {results.success > 0 && (
                      <p className="text-green-600">{results.success} sent successfully</p>
                    )}
                    {results.failed > 0 && (
                      <p className="text-red-600">{results.failed} failed to send</p>
                    )}
                  </div>
                )}
                <button
                  onClick={onClose}
                  className="px-6 py-2 text-sm font-medium text-white bg-brand rounded-lg hover:bg-brand-hover"
                >
                  Done
                </button>
              </div>
            ) : loading ? (
              // Loading State
              <div className="flex items-center justify-center h-full">
                <Loader2 size={32} className="animate-spin text-brand" />
              </div>
            ) : contacts.length === 0 ? (
              // No Contacts State
              <div className="flex flex-col items-center justify-center h-full text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                  <AlertCircle size={32} className="text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No Contacts Available
                </h3>
                <p className="text-sm text-gray-500 max-w-xs">
                  There are no contacts with phone numbers for this school.
                  Add contacts first to send notifications.
                </p>
              </div>
            ) : (
              // Contacts List
              <div>
                {/* Deliverable Info */}
                {deliverable && (
                  <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1">Sending notification for:</p>
                    <p className="text-sm font-medium text-gray-900 line-clamp-2">
                      {deliverable.title}
                    </p>
                  </div>
                )}

                {/* Template Selector */}
                <div className="mb-4">
                  <label className="block text-xs font-medium text-gray-700 mb-2">
                    Select Template
                  </label>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setTemplateDropdownOpen(!templateDropdownOpen)}
                      className="w-full flex items-center justify-between px-3 py-2.5 bg-white border border-gray-300 rounded-lg text-left hover:border-gray-400 transition-colors"
                    >
                      <div className="flex-1 min-w-0">
                        {selectedTemplate ? (
                          <>
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`px-2 py-0.5 text-xs font-medium rounded ${getTypeColor(selectedTemplate.deliverable_type)}`}>
                                {getTypeLabel(selectedTemplate.deliverable_type)}
                              </span>
                              <span className={`px-2 py-0.5 text-xs font-medium rounded ${getStageColor(selectedTemplate.notification_stage)}`}>
                                {getStageLabel(selectedTemplate.notification_stage)}
                              </span>
                            </div>
                            <p className="text-sm text-gray-900 truncate">{selectedTemplate.description}</p>
                          </>
                        ) : (
                          <span className="text-sm text-gray-500">Select a template...</span>
                        )}
                      </div>
                      <ChevronDown size={16} className={`text-gray-400 transition-transform flex-shrink-0 ml-2 ${templateDropdownOpen ? "rotate-180" : ""}`} />
                    </button>

                    {/* Dropdown */}
                    {templateDropdownOpen && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-80 overflow-y-auto">
                        {templates.map((template) => (
                          <button
                            key={template.id}
                            type="button"
                            onClick={() => {
                              setSelectedTemplate(template);
                              setTemplateDropdownOpen(false);
                            }}
                            className={`w-full px-3 py-2.5 text-left hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0 ${
                              selectedTemplate?.id === template.id ? "bg-brand/5" : ""
                            }`}
                          >
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`px-2 py-0.5 text-xs font-medium rounded ${getTypeColor(template.deliverable_type)}`}>
                                {getTypeLabel(template.deliverable_type)}
                              </span>
                              <span className={`px-2 py-0.5 text-xs font-medium rounded ${getStageColor(template.notification_stage)}`}>
                                {getStageLabel(template.notification_stage)}
                              </span>
                            </div>
                            <p className="text-sm text-gray-900">{template.description}</p>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Custom Link Input */}
                <div className="mb-4">
                  <label className="block text-xs font-medium text-gray-700 mb-2">
                    Link <span className="text-gray-400 font-normal">(optional)</span>
                  </label>
                  <input
                    type="url"
                    value={customLink}
                    onChange={(e) => setCustomLink(e.target.value)}
                    placeholder="https://..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-brand focus:border-brand"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Leave empty to send unique review link per contact
                  </p>
                </div>

                {/* Message Preview */}
                {selectedTemplate && (
                  <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-xs text-green-700 font-medium mb-2">Message Preview:</p>
                    <p className="text-sm text-green-800 whitespace-pre-line">
                      {selectedTemplate.message_preview}
                    </p>
                    <p className="text-xs text-green-600 mt-2">
                      Template: <span className="font-mono">{selectedTemplate.template_name}</span>
                    </p>
                  </div>
                )}

                {/* Select All */}
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-gray-700">
                    Select Contacts ({selectedIds.size}/{contacts.length})
                  </span>
                  <button
                    onClick={toggleAll}
                    className="text-sm text-brand hover:underline"
                  >
                    {selectedIds.size === contacts.length ? "Deselect All" : "Select All"}
                  </button>
                </div>

                {/* Contacts */}
                <div className="space-y-2">
                  {contacts.map((contact) => (
                    <label
                      key={contact.id}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        selectedIds.has(contact.id)
                          ? "bg-brand/5 border-brand/30"
                          : "bg-white border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedIds.has(contact.id)}
                        onChange={() => toggleContact(contact.id)}
                        className="w-4 h-4 text-brand rounded border-gray-300 focus:ring-brand"
                      />
                      <div className="w-9 h-9 bg-gray-100 rounded-full flex items-center justify-center">
                        <User size={18} className="text-gray-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900">{contact.name}</p>
                        {contact.designation && (
                          <p className="text-xs text-gray-500">{contact.designation}</p>
                        )}
                        <p className="text-xs text-gray-400">{contact.phone}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          {!sent && !loading && contacts.length > 0 && (
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
              <div className="flex justify-end gap-3">
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSend}
                  disabled={sending || selectedIds.size === 0 || !selectedTemplate}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50"
                >
                  {sending ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <Send size={16} />
                  )}
                  Send to {selectedIds.size} Contact{selectedIds.size !== 1 ? "s" : ""}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotifyClientsSlider;
